import { useCallback, useState } from 'react';
import ProfileImageCrop from './BettieProfileImageCrop';
import ProfileImagePreview from './BettieProfileImagePreview';
import ProfileImageSelect from './BettieProfileImageSelect';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { BettieSettingInfo } from '../../hooks/useBettieSettingInfo';

type ProfileImageStep = 'select' | 'crop' | 'preview';

interface BettieProfileImageProps {
  bettieSettingInfo: BettieSettingInfo;
  updateBettieSettingInfo: (partial: Partial<BettieSettingInfo>) => void;
  setCurrentStep: (step: BettieSettingStep) => void;
  setStepHistory: React.Dispatch<React.SetStateAction<BettieSettingStep[]>>;
}

const BettieProfileImage = ({
  bettieSettingInfo,
  updateBettieSettingInfo,
  setCurrentStep,
  setStepHistory
}: BettieProfileImageProps) => {
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState<string | null>(null);
  const [currentImageStep, setCurrentImageStep] = useState<ProfileImageStep>('select');

  const handleImageSelected = useCallback(
    (imageUrl: string) => {
      setSelectedImageForCrop(imageUrl);
      updateBettieSettingInfo({ originalDataUrl: imageUrl });
      setCurrentImageStep('crop');
    },
    [updateBettieSettingInfo]
  );

  const handleCropComplete = (croppedImageUrl: string) => {
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
    }
    updateBettieSettingInfo({ dataUrl: croppedImageUrl });
    setSelectedImageForCrop(null);
    setCurrentImageStep('preview');
  };

  const handleBackFromCrop = () => {
    setSelectedImageForCrop(null);
    setCurrentImageStep('select');
  };

  const handleBackFromPreview = () => {
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
    }
    updateBettieSettingInfo({ dataUrl: null });
    setCurrentImageStep('select');
  };

  const handleConfirmPreview = () => {
    setCurrentStep('user_nickname');
  };

  return (
    <div className="bg-pure-black min-h-screen pt-[env(safe-area-inset-top)] fixed inset-0 flex flex-col items-center justify-center px-[24px] overflow-hidden">
      {currentImageStep === 'select' && (
        <ProfileImageSelect
          bettieName={bettieSettingInfo.bettieNickname}
          onImageSelected={handleImageSelected}
          setPreviewObjectUrl={setPreviewObjectUrl}
          setCurrentStep={setCurrentStep}
          setStepHistory={setStepHistory}
        />
      )}
      {currentImageStep === 'crop' && selectedImageForCrop && (
        <ProfileImageCrop
          imageSrc={selectedImageForCrop}
          onCropComplete={handleCropComplete}
          onBack={handleBackFromCrop}
        />
      )}
      {currentImageStep === 'preview' && bettieSettingInfo.dataUrl && (
        <ProfileImagePreview
          bettieProfileImage={bettieSettingInfo.dataUrl}
          previewObjectUrl={previewObjectUrl}
          clearBettieProfileImage={handleBackFromPreview}
          onConfirm={handleConfirmPreview}
        />
      )}
    </div>
  );
};

export default BettieProfileImage;
